import { clampInput, type PlayerInput } from "@meleeroyale/sim";
import { Reader, Writer } from "./bytes.ts";
import { MessageType } from "./types.ts";

export const MAX_INPUTS_PER_MESSAGE = 4;

/** `viewLag` byte meaning "the present": no lag compensation asked for. */
const VIEW_PRESENT = 255;

/**
 * [type u8][count u8] then per input: tick u32, moveX i8, moveY i8, yaw u16,
 * pitch i8, buttons u16, viewLag u8 (tick − viewTick, 0..254; 255 = present).
 */
export function encodeInputs(inputs: readonly PlayerInput[]): Uint8Array<ArrayBuffer> {
  const w = new Writer(2 + 12 * MAX_INPUTS_PER_MESSAGE);
  const n = Math.min(inputs.length, MAX_INPUTS_PER_MESSAGE);
  w.u8(MessageType.Input);
  w.u8(n);
  for (let i = 0; i < n; i += 1) {
    const input = inputs[i];
    if (!input) continue;
    w.u32(input.tick);
    w.i8(input.moveX);
    w.i8(input.moveY);
    w.u16(input.yaw);
    w.i8(input.pitch);
    w.u16(input.buttons);
    const lag = input.tick - input.viewTick;
    w.u8(input.viewTick <= 0 ? VIEW_PRESENT : lag < 0 ? 0 : lag > 254 ? 254 : lag);
  }
  return w.bytes();
}

/** Returns clamped inputs, or null when the frame is malformed. */
export function decodeInputs(bytes: Uint8Array): PlayerInput[] | null {
  try {
    const r = new Reader(bytes);
    if (r.u8() !== MessageType.Input) return null;
    const n = r.u8();
    if (n > MAX_INPUTS_PER_MESSAGE) return null;
    const out: PlayerInput[] = [];
    for (let i = 0; i < n; i += 1) {
      const tick = r.u32();
      const moveX = r.i8();
      const moveY = r.i8();
      const yaw = r.u16();
      const pitch = r.i8();
      const buttons = r.u16();
      const lag = r.u8();
      const viewTick = lag === VIEW_PRESENT ? 0 : Math.max(0, tick - lag);
      out.push(clampInput({ tick, moveX, moveY, yaw, pitch, buttons, viewTick }));
    }
    return r.remaining === 0 ? out : null;
  } catch {
    return null;
  }
}
